import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pageTitle } from '../../helper';
import Cta from '../Cta';
import Div from '../Div';
import PageHeading from '../PageHeading';
import Spacing from '../Spacing';
import SectionHeading from '../SectionHeading';
import { Helmet } from 'react-helmet';

export default function FaqPage() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const prefix = isEn ? '/en' : '';

  pageTitle(t('faq.pageTitle'));

  const faqData = t('faq.categories', { returnObjects: true });

  // Ve výchozím stavu rozklikneme všechny otázky (lepší UX + SEO)
  const allKeys = faqData.flatMap((s, sIdx) =>
    s.questions.map((_, qIdx) => `${sIdx}-${qIdx}`)
  );
  const [openItems, setOpenItems] = useState(new Set(allKeys));

  const toggleItem = (key) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQPage strukturovaná data pro Google rich snippets
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.flatMap((section) =>
      section.questions.map((q) => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: q.answer,
        },
      }))
    ),
  };

  return (
    <>
      <Helmet>
        <title>{t('faq.metaTitle')}</title>
        <link rel="icon" href="/stablerecords.ico" />
        <meta
          name="description"
          content={t('faq.metaDescription')}
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <PageHeading
        title={t('faq.pageTitle')}
        bgSrc="/images/about_hero_bg.jpeg"
        pageLinkText={t('faq.pageLinkText')}
      />
      <Spacing lg="80" md="50" />
      <Div className="container">
        <SectionHeading
          title={t('faq.sectionTitle')}
          subtitle={t('faq.sectionSubtitle')}
          variant="cs-style1 text-center"
        />
        <Spacing lg="60" md="40" />

        {faqData.map((section, sIdx) => (
          <Div key={sIdx} className="cs-faq_section mb-5">
            <h3 className="cs-font_30 cs-m0 mb-4">{section.category}</h3>
            {section.questions.map((item, qIdx) => {
              const key = `${sIdx}-${qIdx}`;
              const isOpen = openItems.has(key);
              return (
                <Div
                  key={qIdx}
                  className={`cs-accordian cs-style1 mb-3 ${isOpen ? 'active' : ''}`}
                >
                  <Div
                    className="cs-accordian_head"
                    onClick={() => toggleItem(key)}
                    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                  >
                    <h6 className="cs-accordian_title cs-semi_bold cs-primary_font cs-font_18 cs-m0">
                      {item.question}
                    </h6>
                    <span className="cs-accordian_toggle cs-accent_color">
                      <svg
                        width={15}
                        height={8}
                        viewBox="0 0 15 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M0 0L7.5 7.5L15 0H0Z" fill="currentColor" />
                      </svg>
                    </span>
                  </Div>
                  <Div className="cs-accordian_body">
                    <Div className="cs-accordian_body_in">{item.answer}</Div>
                  </Div>
                </Div>
              );
            })}
            <Spacing lg="30" md="20" />
          </Div>
        ))}
      </Div>
      <Spacing lg="80" md="50" />
      <Div className="container">
        <Cta
          title={t('faq.ctaTitle')}
          btnText={t('faq.ctaBtn')}
          btnLink={'/kontakt'}
          bgSrc="/images/cta_bg.jpeg"
        />
      </Div>
    </>
  );
}